import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Edit2, Trash2, Warehouse } from 'lucide-react';
import { DataTable } from '../../../../../components/ui/DataTable';
import { ConfirmDialog } from '../../../../../components/ui/ConfirmDialog';
import { AssignWarehouseModal } from './AssignWarehouseModal';
import {
  fetchZoneAssignments,
  deleteAssignment,
} from '../../../../../features/zones/zonesThunks';
import { getShippingMethodLabel } from '../../../../../utils/catalogs';

/**
 * Warehouses tab: the warehouses served by this zone and the shipping method
 * each one operates under.
 *
 * The endpoint returns a plain array, so the table paginates client-side.
 *
 * @param {object} props
 * @param {number|string} props.zoneId
 */
export const ZoneAssignmentsTab = ({ zoneId }) => {
  const dispatch = useDispatch();
  const { assignments, loadingAssignments, savingAssignment } = useSelector(
    (state) => state.zones
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);

  const handleOpenCreate = () => {
    setEditingAssignment(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (assignment) => {
    setEditingAssignment(assignment);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteAssignment(assignmentToDelete.id))
      .unwrap()
      .then(() => {
        setAssignmentToDelete(null);
        dispatch(fetchZoneAssignments(zoneId));
      })
      .catch(() => setAssignmentToDelete(null));
  };

  const columns = [
    {
      header: 'BODEGA',
      cell: (row) => (
        <span className="font-semibold text-ninja-dark dark:text-white">
          {row.warehouse_name || '-'}
        </span>
      ),
    },
    { header: 'CLIENTE', cell: (row) => row.company_name || '-' },
    {
      header: 'MÉTODO DE ENVÍO',
      cell: (row) => (
        <span className="px-2.5 py-1 rounded-lg bg-ninja-greenLight text-ninja-green text-xs font-bold">
          {getShippingMethodLabel(row.shipping_method)}
        </span>
      ),
    },
    {
      header: 'ACCIONES',
      cell: (row) => (
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => handleOpenEdit(row)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-ninja-dark dark:hover:text-white hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] transition-colors"
            title="Editar método de envío"
          >
            <Edit2 size={16} />
          </button>
          <button
            onClick={() => setAssignmentToDelete(row)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-danger hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title="Eliminar asignación"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border overflow-hidden animate-in slide-in-from-bottom-2 duration-300">
        <div className="p-4 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <Warehouse size={16} className="text-text-muted" />
            {assignments.length === 0
              ? 'Esta zona no está asignada a ninguna bodega.'
              : `${assignments.length} ${assignments.length === 1 ? 'bodega asignada' : 'bodegas asignadas'}`}
          </div>
          <button
            onClick={handleOpenCreate}
            className="flex items-center gap-2 px-5 py-2.5 bg-ninja-green hover:bg-ninja-green/90 text-white font-bold rounded-xl transition-all shadow-sm whitespace-nowrap text-sm"
          >
            <Plus size={16} />
            Asignar Bodega
          </button>
        </div>

        <DataTable
          columns={columns}
          data={assignments}
          totalItems={assignments.length}
          currentPage={1}
          itemsPerPage={100}
          isLoading={loadingAssignments}
          itemLabel="asignaciones"
          onPageChange={() => {}}
          onRowsPerPageChange={() => {}}
        />
      </div>

      <AssignWarehouseModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingAssignment(null);
        }}
        zoneId={zoneId}
        assignment={editingAssignment}
        onSaved={() => dispatch(fetchZoneAssignments(zoneId))}
      />

      <ConfirmDialog
        isOpen={Boolean(assignmentToDelete)}
        onClose={() => setAssignmentToDelete(null)}
        onConfirm={handleConfirmDelete}
        loading={savingAssignment}
        title="Eliminar asignación"
        message={`¿Seguro que quieres desasignar la bodega "${assignmentToDelete?.warehouse_name}" de esta zona?`}
      />
    </>
  );
};
